import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { AuthSession, AuthUser, LoginRequest } from "@al-life/types";
import { api, type ApiResult } from "@/lib/api";
import { setCacheOwner } from "@/lib/apiCache";
import { clearToken, getToken, setToken } from "@/lib/authStore";

/**
 * Global auth state.
 *
 * - On mount, if a token exists we call `GET /auth/me` to rehydrate the user.
 * - `login` / `applySession` persist the token and set the user.
 * - `isAdmin` is derived from the user's role — gate edit controls on this.
 *
 * The user object is the source of truth for the role; it's always fetched
 * fresh from the server, so promoting someone to admin in MongoDB takes effect
 * on their next page load / API call (no code change, no re-deploy).
 */

type AuthStatus = "loading" | "authenticated" | "anonymous";

interface AuthContextValue {
  user: AuthUser | null;
  status: AuthStatus;
  isAdmin: boolean;
  login: (body: LoginRequest) => Promise<ApiResult<AuthSession>>;
  applySession: (session: AuthSession) => void;
  setUser: (user: AuthUser) => void;
  logout: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUserState(null);
      setStatus("anonymous");
      setCacheOwner("anon");
      return;
    }
    const res = await api.auth.me();
    if (res.success && res.data) {
      setUserState(res.data);
      setStatus("authenticated");
      setCacheOwner(res.data.id);
    } else {
      // Token is invalid/expired — drop it.
      clearToken();
      setUserState(null);
      setStatus("anonymous");
      setCacheOwner("anon");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const applySession = useCallback((session: AuthSession) => {
    setToken(session.token);
    setUserState(session.user);
    setStatus("authenticated");
    setCacheOwner(session.user.id);
  }, []);

  const login = useCallback(async (body: LoginRequest) => {
    const res = await api.auth.login(body);
    if (res.success && res.data) {
      setToken(res.data.token);
      setUserState(res.data.user);
      setStatus("authenticated");
      setCacheOwner(res.data.user.id);
    }
    return res;
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUserState(null);
    setStatus("anonymous");
    setCacheOwner("anon");
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      isAdmin: user?.role === "admin",
      login,
      applySession,
      setUser: setUserState,
      logout,
      refresh,
    }),
    [user, status, login, applySession, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an <AuthProvider>");
  }
  return ctx;
}

/** Convenience hook for gating edit controls. */
export function useIsAdmin(): boolean {
  return useAuth().isAdmin;
}
